import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Sparkles } from 'lucide-react';
import { showErrorToast, showSuccessToast } from '@/services/toastService';
import { useAuth } from '@/auth/AuthProvider';
import type {
    JacintaMemoryEntry,
    ReportKind,
    ReviewerScoreEdit,
} from '../types';
import { reportsApi } from '../services/reports.api';
import { addJacintaMemory } from '../services/memory-shim';
import { EditScorePopover } from './EditScorePopover';

export interface EditableScoreProps {
    reportKind: ReportKind;
    resultId: string;
    componentId: string;
    componentLabel: string;
    scale: { min: number; max: number; labels?: string[] };
    binary?: boolean;
    aiScore: number;
    aiJustification?: string;
    edits: ReviewerScoreEdit[];
    size?: 'sm' | 'md' | 'lg';
    jobId?: string;
    taskId?: string;
    onEdited?: (edit: ReviewerScoreEdit) => void;
}

const TOAST_SUCCESS = 'Score updated. Jacinta learned from this — applying to future similar candidates.';
const TOAST_FAILURE = "Couldn't save. Try again.";

function relativeTime(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(ms)) return '';
    const sec = Math.round(ms / 1000);
    if (sec < 60) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.round(hr / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.round(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.round(months / 12)}y ago`;
}

function latestEditFor(edits: ReviewerScoreEdit[], componentId: string): ReviewerScoreEdit | undefined {
    let latest: ReviewerScoreEdit | undefined;
    for (const e of edits) {
        if (e.componentId !== componentId) continue;
        if (!latest || new Date(e.editedAt) >= new Date(latest.editedAt)) latest = e;
    }
    return latest;
}

export function EditableScore({
    reportKind,
    resultId,
    componentId,
    componentLabel,
    scale,
    binary,
    aiScore,
    aiJustification,
    edits,
    size = 'md',
    jobId,
    taskId,
    onEdited,
}: EditableScoreProps) {
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [optimistic, setOptimistic] = useState<ReviewerScoreEdit | null>(null);
    const { user } = useAuth();

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [popoverHost, setPopoverHost] = useState<HTMLElement | null>(null);

    // Resolve the popover host once on mount — an ancestor marked with
    // `data-editable-score-host`. The popover is portaled into that host
    // (rendered as the last child of the row card) instead of nested inside
    // the score column, so opening it doesn't inflate the column and squeeze
    // the title block until its text wraps letter-by-letter.
    useLayoutEffect(() => {
        const host = wrapperRef.current?.closest<HTMLElement>('[data-editable-score-host]') ?? null;
        setPopoverHost(host);
    }, []);

    const latest = optimistic ?? latestEditFor(edits, componentId);
    const effectiveScore = latest ? latest.editedScore : aiScore;
    const isEdited = !!latest;

    const aiLabel = useMemo(() => {
        if (binary) return aiScore === 1 ? 'Pass' : 'Fail';
        if (scale.labels) return scale.labels[aiScore - scale.min];
        return undefined;
    }, [aiScore, binary, scale.labels, scale.min]);

    const effectiveLabel = useMemo(() => {
        if (binary) return effectiveScore === 1 ? 'Pass' : 'Fail';
        if (scale.labels) return scale.labels[effectiveScore - scale.min];
        return undefined;
    }, [effectiveScore, binary, scale.labels, scale.min]);

    const fontSize = size === 'lg' ? 26 : size === 'sm' ? 13 : 16;
    const labelSize = size === 'lg' ? 13 : size === 'sm' ? 11 : 12;

    const onSave = async (editedScore: number, justification: string) => {
        setSaving(true);
        const reviewerId = user?._id ?? 'me';
        const reviewerName =
            (user?.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : null) ||
            user?.email ||
            'You';
        const editedAt = new Date().toISOString();

        // Optimistic local update. Tag the edit with `reportKind` so parent
        // routers (e.g. ApplicationFullPager) can append it to the correct
        // local result (CV vs AI) and trigger headline recomputation.
        const optimisticEntry: ReviewerScoreEdit = {
            reviewerId,
            reviewerName,
            componentId,
            originalScore: aiScore,
            editedScore,
            justification,
            editedAt,
            reportKind,
        };
        setOptimistic(optimisticEntry);

        try {
            await reportsApi.editScore({
                kind: reportKind,
                resultId,
                componentId,
                editedScore,
                justification,
            });

            // Push the memory entry per the YC-demo contract.
            const firstName = user?.firstName ?? reviewerName.split(' ')[0] ?? 'You';
            const memoryEntry: JacintaMemoryEntry = {
                id:
                    typeof crypto !== 'undefined' && 'randomUUID' in crypto
                        ? crypto.randomUUID()
                        : `mem-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                createdAt: editedAt,
                category: 'scoring',
                attributedTo: {
                    name: reviewerName,
                    avatarUrl: '',
                    role: 'Hiring manager',
                },
                trigger: `When scoring ${componentLabel} for ${
                    reportKind === 'cv_screening' ? 'CV screening' : 'AI interview'
                } candidates`,
                insight: `${firstName} scored ${editedScore} (vs my default ${aiScore}) — applying to future similar candidates.`,
                appliesTo: [reportKind],
                appliedCount: 0,
                source: 'score_edit',
                relatedJobIds: jobId ? [jobId] : undefined,
                relatedTaskId: taskId,
            };
            addJacintaMemory(memoryEntry);

            showSuccessToast(TOAST_SUCCESS, { autoClose: 6000 });
            onEdited?.(optimisticEntry);
            setEditing(false);
        } catch (err) {
            console.error('Failed to save score edit', err);
            setOptimistic(null);
            showErrorToast(TOAST_FAILURE);
        } finally {
            setSaving(false);
        }
    };

    const popover = editing ? (
        <EditScorePopover
            componentLabel={componentLabel}
            aiScore={aiScore}
            aiScoreLabel={aiLabel}
            aiJustification={aiJustification}
            currentScore={effectiveScore}
            scaleMin={scale.min}
            scaleMax={scale.max}
            scaleLabels={scale.labels}
            binary={binary}
            saving={saving}
            onCancel={() => setEditing(false)}
            onSave={onSave}
        />
    ) : null;

    return (
        // Score row stays narrow (inline-flex, no w-full) so the adjacent
        // title block keeps its width. The editing popover is portaled into
        // the row's host ancestor below, not nested inside this column.
        <div
            ref={wrapperRef}
            className="group inline-flex items-center gap-2 whitespace-nowrap shrink-0"
        >
            <span
                className="tabular-nums"
                style={{
                    fontSize,
                    fontFamily: 'var(--v2-font-serif)',
                    fontWeight: 500,
                    color: isEdited ? 'var(--accent-emphasis)' : 'var(--text-primary)',
                }}
            >
                {binary ? (effectiveScore === 1 ? 'Pass' : 'Fail') : effectiveScore}
            </span>
            {!binary && (
                <span
                    className="tabular-nums"
                    style={{ fontSize: labelSize, color: 'var(--text-tertiary)' }}
                >
                    / {scale.max}
                </span>
            )}
            {effectiveLabel && !binary && (
                <span
                    className="text-[11.5px]"
                    style={{ color: 'var(--text-secondary)' }}
                >
                    — {effectiveLabel}
                </span>
            )}
            <button
                onClick={() => setEditing((v) => !v)}
                className="w-6 h-6 flex items-center justify-center rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ color: 'var(--text-quaternary)' }}
                title="Edit score"
                aria-label="Edit score"
            >
                <Pencil size={11} strokeWidth={1.75} />
            </button>
            {isEdited && latest && (
                <span
                    className={
                        size === 'sm'
                            ? 'inline-flex items-center justify-center w-4 h-4 rounded-full'
                            : 'inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded'
                    }
                    style={{
                        background: 'var(--accent-subtle)',
                        color: 'var(--accent-emphasis)',
                        border: '1px solid var(--border-accent)',
                    }}
                    title={`Edited by you ${relativeTime(latest.editedAt)} · AI scored ${aiScore}${
                        aiLabel ? ` (${aiLabel})` : ''
                    }, you edited to ${latest.editedScore}${
                        effectiveLabel ? ` (${effectiveLabel})` : ''
                    }.\n${latest.justification}`}
                >
                    <Sparkles size={size === 'sm' ? 9 : 9} strokeWidth={2.5} />
                    {size !== 'sm' && <>Edited by you · {relativeTime(latest.editedAt)}</>}
                </span>
            )}
            {popover && popoverHost && createPortal(popover, popoverHost)}
            {popover && !popoverHost && popover}
        </div>
    );
}
